import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useApp } from "../context/AppContext";
import { PageHeader, TableShell, EmptyRow, StatusBadge } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TABS = [
  { id: "all", type: null, status: null },
  { id: "income", type: "income", status: null },
  { id: "expense", type: "expense", status: null },
  { id: "receivables", type: "income", status: "pending" },
  { id: "payables", type: "expense", status: "pending" },
];
const emptyTx = { type: "income", category: "", description: "", amount: "", status: "paid", due_date: "" };

export default function Financial() {
  const { t, fmt } = useApp();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyTx);

  const load = useCallback(() => {
    const cfg = TABS.find((x) => x.id === tab);
    const params = {};
    if (cfg.type) params.type = cfg.type;
    if (cfg.status) params.status = cfg.status;
    api.get("/transactions", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", {
        ...form,
        amount: parseFloat(form.amount),
        due_date: form.due_date || null,
      });
      toast.success(t("saved")); setOpen(false); setForm(emptyTx); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const markPaid = async (id) => {
    try { await api.patch(`/transactions/${id}/pay`); toast.success(t("saved")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/transactions/${id}`); toast.success(t("deleted")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const income = rows.filter((r) => r.type === "income" && r.status === "paid").reduce((s, r) => s + r.amount, 0);
  const expense = rows.filter((r) => r.type === "expense" && r.status === "paid").reduce((s, r) => s + r.amount, 0);

  return (
    <div>
      <PageHeader title={t("financial")}>
        <Button data-testid="add-transaction-button" onClick={() => { setForm(emptyTx); setOpen(true); }}
          className="h-11 bg-sky-600 hover:bg-sky-700 text-white font-semibold">
          <Plus size={18} className="mr-2" /> {t("addTransaction")}
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">{t("income")}</div>
          <div data-testid="fin-income-total" className="font-heading text-2xl font-bold text-emerald-600">{fmt(income)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">{t("expense")}</div>
          <div data-testid="fin-expense-total" className="font-heading text-2xl font-bold text-red-500">{fmt(expense)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1">{t("net")}</div>
          <div data-testid="fin-net-total" className="font-heading text-2xl font-bold text-slate-900">{fmt(income - expense)}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(({ id }) => (
          <button key={id} data-testid={`fin-tab-${id}`} onClick={() => setTab(id)}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
              tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {t(id === "all" ? "all" : id === "receivables" ? "receivables" : id === "payables" ? "payables" : id)}
          </button>
        ))}
      </div>
      <TableShell headers={[t("date"), t("type"), t("category"), t("description"), t("amount"), t("status"), t("actions")]}>
        {rows.length === 0 && <EmptyRow colSpan={7} />}
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{(r.date || "").slice(0, 10)}</td>
            <td className="px-4 py-3"><StatusBadge status={r.type} /></td>
            <td className="px-4 py-3">{r.category}</td>
            <td className="px-4 py-3 text-slate-500">{r.description}</td>
            <td className={`px-4 py-3 font-semibold ${r.type === "income" ? "text-emerald-600" : "text-red-500"}`}>
              {r.type === "income" ? "+" : "−"}{fmt(r.amount)}
            </td>
            <td className="px-4 py-3"><StatusBadge status={r.status === "paid" ? "paid" : "pending"} /></td>
            <td className="px-4 py-3">
              <div className="flex gap-1">
                {r.status === "pending" && (
                  <Button data-testid={`mark-paid-${r.id}`} variant="ghost" size="icon" title={t("markPaid")}
                    className="text-emerald-600 hover:text-emerald-800" onClick={() => markPaid(r.id)}>
                    <CheckCircle2 size={15} />
                  </Button>
                )}
                <Button data-testid={`delete-tx-${r.id}`} variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => remove(r.id)}>
                  <Trash2 size={15} />
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </TableShell>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t("addTransaction")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("type")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type-select" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t("income")}</SelectItem>
                    <SelectItem value="expense">{t("expense")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="tx-status-select" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">{t("paid_status")}</SelectItem>
                    <SelectItem value="pending">{t("pending")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("category")}</Label>
              <Input data-testid="tx-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-11" placeholder="Parts / Rent / Sale…" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("description")}</Label>
              <Input data-testid="tx-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-11" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("amount")} *</Label>
                <Input data-testid="tx-amount-input" required type="number" step="0.01" min="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-11" />
              </div>
              {form.status === "pending" && (
                <div className="space-y-1.5">
                  <Label>{t("dueDate")}</Label>
                  <Input data-testid="tx-due-date-input" type="date" value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-11" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)}>{t("cancel")}</Button>
              <Button data-testid="tx-save-button" type="submit" className="h-11 bg-sky-600 hover:bg-sky-700 text-white">{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
